import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Newspaper, Clock, Sparkles, Home as HomeIcon, Car } from "lucide-react";
import { motion } from "framer-motion";

const TABS = [
  { key: "property", label: "Punjab Property", icon: HomeIcon },
  { key: "cars", label: "Car Market", icon: Car },
];

export default function NewsSection() {
  const [category, setCategory] = useState("property");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/news?category=${category}`).then((r) => {
      if (active) { setArticles(r.data.articles || []); setLoading(false); }
    }).catch(() => active && setLoading(false));
    return () => { active = false; };
  }, [category]);

  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20" data-testid="news-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-dark flex items-center gap-1.5">
            <Newspaper className="h-3.5 w-3.5" /> Latest news
          </p>
          <h2 className="mt-2 font-display text-3xl lg:text-4xl tracking-tight font-semibold text-slate-900">Market pulse & updates</h2>
        </div>
        <div className="flex bg-slate-100 rounded-lg p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`news-tab-${t.key}`}
              onClick={() => setCategory(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${category === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {articles.map((a, i) => (
            <motion.article
              key={i}
              data-testid={`news-card-${i}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-navy/20 transition-all"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold bg-navy/5 text-navy px-2 py-0.5 rounded-full">{a.tag || "News"}</span>
                <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {a.read_time || "2 min read"}</span>
              </div>
              <h3 className="mt-2 font-display text-lg font-semibold text-slate-900 leading-snug">{a.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{a.summary}</p>
            </motion.article>
          ))}
        </div>
      )}
      <p className="mt-4 text-xs text-slate-400 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" /> AI-generated market insights — refreshed regularly.
      </p>
    </section>
  );
}
